// Discount Code API Service
// Handles discount code validation and management
// =====================================================

import { API_BASE_URL } from '../config/apiEndpoints';

class DiscountCodeApiService {
  getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
  /**
   * Validate a discount code for a given amount
   * @param {string} code - The discount code to validate
   * @param {number} amount - The total amount before discount
   * @returns {Promise<Object>} Validation response with discount details
   */
  async validateDiscountCode(code, amount) {
    try {
      const url = `${API_BASE_URL}/discount-codes/validate?code=${encodeURIComponent(code)}&amount=${encodeURIComponent(amount)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        }
      });

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        let errorMessage = 'Invalid discount code';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.detail || errorMessage;
        } catch (parseError) {
          // If we can't parse the error, use status-based message
          if (response.status === 404) {
            errorMessage = 'Discount code not found';
          } else if (response.status === 400) {
            errorMessage = 'Invalid discount code format';
          } else {
            errorMessage = 'This discount code could not be checked. Please try again.';
          }
        }
        
        return {
          is_valid: false,
          message: errorMessage,
          error: true
        };
      }

      const data = await response.json();

      return {
        is_valid: data.is_valid || false,
        message: data.message || '',
        discount_type: data.discount_type,
        discount_value: data.discount_value,
        discount_amount: data.discount_amount || 0,
        discounted_total: data.discounted_total || amount,
        status: data.status,
        expires_at: data.expires_at,
        error: false
      };
    } catch (error) {

      // Check if it's a CORS error
      if (error.message && (
        error.message.includes('CORS') || 
        error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError') ||
        error.name === 'TypeError'
      )) {
        return {
          is_valid: false,
          message: 'We couldn’t check this discount code. Please try again.',
          error: true,
          corsError: true
        };
      }
      
      return {
        is_valid: false,
        message: 'Failed to validate discount code. Please check your connection and try again.',
        error: true
      };
    }
  }

  /**
   * Create a new discount code (admin function)
   * @param {Object} codeData - Discount code data
   * @returns {Promise<Object>} Created discount code
   */
  async createDiscountCode(codeData) {
    try {
      const response = await fetch(`${API_BASE_URL}/discount-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        },
        body: JSON.stringify(codeData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.detail || 'Failed to create discount code');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get discount code by ID
   * @param {string} codeId - The discount code ID
   * @returns {Promise<Object>} Discount code details
   */
  async getDiscountCodeById(codeId) {
    try {
      const response = await fetch(`${API_BASE_URL}/discount-codes/${codeId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.detail || 'Failed to fetch discount code');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * List discount codes (admin function)
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} List of discount codes
   */
  async listDiscountCodes(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.include_expired !== undefined) params.append('include_expired', filters.include_expired);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.venue_id) params.append('venue_id', filters.venue_id);

      const url = `${API_BASE_URL}/discount-codes${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.detail || 'Failed to fetch discount codes');
      }

      const data = await response.json();
      return Array.isArray(data) ? data : (data?.items ?? data?.discount_codes ?? []);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete a discount code
   * @param {string} codeId - The discount code ID
   */
  async deleteDiscountCode(codeId) {
    try {
      const response = await fetch(`${API_BASE_URL}/discount-codes/${codeId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.detail || 'Failed to delete discount code');
      }
    } catch (error) {
      throw error;
    }
  }
}

const discountCodeApiService = new DiscountCodeApiService();
export default discountCodeApiService;
